// Ticket für Hausmeister freigeben (Verwaltungsseite).
//
// Ohne Freigabe sieht ein Hausmeister das Ticket überhaupt nicht — die Freigabe
// ist also kein Hinweis, sondern der Schalter für die Sichtbarkeit.
// Läuft als Tab im „Einsatz“-Widget: Titel und Karte liefert das Widget.
import React, { useCallback, useEffect, useState } from 'react';
import { Broom, SpinnerGap, UserCircle, WarningCircle, X } from '@phosphor-icons/react';
import {
  freigabeEntziehen, freigeben, ladeFreigaben, ladeHausmeister,
} from '../../lib/hausmeister/api';
import { AppKachel } from '../ui/AppIconKnopf';

interface Props {
  ticketId: string;
  userProfile?: { id?: string } | null;
  /** Anzahl Freigaben (Zähler im Einsatz-Tab) */
  onAnzahl?: (n: number) => void;
  /** Gibt es Hausmeister-Konten oder Freigaben? Sonst kann das Widget den Tab ausblenden. */
  onVerfuegbar?: (ja: boolean) => void;
}

const TicketHausmeister: React.FC<Props> = ({ ticketId, userProfile, onAnzahl, onVerfuegbar }) => {
  const [alle, setAlle] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [freigaben, setFreigaben] = useState<{ id: string; hausmeister_id: string; hinweis: string | null }[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [waehlen, setWaehlen] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    const [h, f] = await Promise.all([ladeHausmeister(), ladeFreigaben(ticketId)]);
    setAlle(h);
    setFreigaben(f);
    setGeladen(true);
  }, [ticketId]);

  useEffect(() => { void laden(); }, [laden]);

  const verfuegbar = alle.length > 0 || freigaben.length > 0;
  useEffect(() => { onAnzahl?.(freigaben.length); }, [freigaben.length, onAnzahl]);
  // Erst nach dem Laden melden — sonst verschwände der Tab kurz beim Öffnen.
  useEffect(() => { if (geladen) onVerfuegbar?.(verfuegbar); }, [geladen, verfuegbar, onVerfuegbar]);

  // Ohne angelegte Hausmeister-Konten ist der Abschnitt nur Rauschen.
  if (!verfuegbar) return null;

  const name = (id: string) => {
    const p = alle.find((x) => x.id === id);
    return p?.full_name || p?.email || 'Hausmeister';
  };
  const offen = alle.filter((h) => !freigaben.some((f) => f.hausmeister_id === h.id));

  const freigabeSetzen = async (hausmeisterId: string) => {
    setArbeitet(true);
    const problem = await freigeben(ticketId, hausmeisterId, userProfile?.id || '');
    setArbeitet(false);
    if (problem) setFehler(problem);
    else { setWaehlen(false); setFehler(null); await laden(); }
  };

  const entziehen = async (f: { id: string; hausmeister_id: string }) => {
    if (!window.confirm(`Freigabe für ${name(f.hausmeister_id)} entziehen? Der Auftrag verschwindet dann aus seiner Ansicht.`)) return;
    await freigabeEntziehen(f.id);
    await laden();
  };

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 12.5, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="flex-1">{fehler}</span>
          <button type="button" onClick={() => setFehler(null)} aria-label="Hinweis schließen" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>
            <X size={13} weight="bold" />
          </button>
        </div>
      )}

      {freigaben.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Noch niemand freigegeben — der Auftrag ist für Hausmeister unsichtbar.</p>
      ) : (
        <div className="flex flex-col">
          {freigaben.map((f) => (
            <div key={f.id} className="flex items-center" style={{ gap: 10, padding: '8px 2px', borderBottom: '1px solid var(--line)', fontSize: 12.5 }}>
              <UserCircle size={20} weight="fill" className="shrink-0" style={{ color: 'var(--acc)' }} />
              <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 800 }}>{name(f.hausmeister_id)}</span>
              <span className="shrink-0" style={{ height: 20, padding: '0 7px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, lineHeight: '20px', background: 'var(--ok-soft)', color: 'var(--ok)' }}>freigegeben</span>
              <button type="button" aria-label="Freigabe entziehen" data-tip="Freigabe entziehen – Hausmeister sieht das Ticket nicht mehr"
                onClick={() => void entziehen(f)}
                className="inline-flex shrink-0 items-center justify-center" style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                <X size={13} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {waehlen && offen.length > 0 && (
        <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1.5px var(--acc)' }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>
            Hausmeister wählen
            {arbeitet && <SpinnerGap size={12} className="animate-spin" />}
            <button type="button" onClick={() => setWaehlen(false)} aria-label="Auswahl schließen"
              className="ml-auto inline-flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
              <X size={12} weight="bold" />
            </button>
          </span>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {offen.map((h) => (
              <button key={h.id} type="button" disabled={arbeitet} onClick={() => void freigabeSetzen(h.id)} data-tip="Hausmeister wählen"
                style={{ height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: arbeitet ? 'not-allowed' : 'pointer', background: 'var(--bg)',
                  boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, opacity: arbeitet ? 0.5 : 1 }}>
                {h.full_name || h.email}
              </button>
            ))}
          </div>
        </div>
      )}

      {offen.length > 0 && !waehlen && (
        <AppKachel icon={Broom} farbe="gruen" titel="Freigeben" unter="Für Hausmeister sichtbar machen"
          tip="Freigeben – Ticket in der App des Hausmeisters sichtbar machen"
          onClick={() => setWaehlen(true)} />
      )}
    </div>
  );
};

export default TicketHausmeister;
